import { useChatbotCRUDActions } from '@mm/state/actions/ChatbotCRUDActions';
import { selectMainMenu } from '@mm/state/actions/viewActions';
import { DefaultResponseType } from '@mm/state/model/ChatbotModel';
import { activeFacebookPagePlanInfoSelector } from '@mm/state/selectors/facebookPageSelector';
import {
  activeChatbotIdSelector,
  activeChatbotSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormGroup, Input, Label } from 'reactstrap';
import { Dispatch } from 'redux';
import useRouter from 'use-react-router';
import Select from '../../../../../../../common/components/MainSelect/MainSelect';
import * as routes from '../../../../../../../common/constants/routes';
import SettingSection from '../../../../components/SettingSection/SettingSection';
import '../../../../components/sharedStyle.scss';
import { ChatBotQandAOptions } from './ChatBotQandAOptions';
import { DEFAULT_RESPONSES, noMatchResponsesMap } from './noMatchResponses';
import './ResponseSettings.scss';

type ResponseOptionProps = {
  handleChange: () => void;
  checked: boolean;
  text: string;
  note: JSX.Element;
  disabled: boolean;
  isDefaultResponse: boolean;
  value: DefaultResponseType;
  onSelectChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const ResponseOption = ({
  handleChange,
  checked,
  text,
  note,
  disabled,
  isDefaultResponse,
  value,
  onSelectChange,
}: ResponseOptionProps) => (
  <Label className="response-settings__label">
    <div className="response-settings__label--content">
      <Input
        className="response-settings__label--input"
        type="radio"
        onChange={handleChange}
        checked={checked}
        disabled={disabled}
      />
      {text}
      {isDefaultResponse && (
        <div>
          &nbsp;
          <Select
            disabled={checked !== isDefaultResponse}
            onChange={onSelectChange}
            value={value}
            className="response-settings__default-response-select"
          >
            {Object.keys(DEFAULT_RESPONSES).map(k => (
              <option value={k} key={k}>
                {DEFAULT_RESPONSES[k]}
              </option>
            ))}
          </Select>
        </div>
      )}
    </div>
    {note && <div className="response-settings__label--note">{note}</div>}
  </Label>
);

const NoteWithLink = ({
  note,
  link,
}: {
  note?: string;
  link?: JSX.Element;
}) => (
  <div className="response-settings__label--note">
    {note}
    {link || null}
  </div>
);

type LinkProps = {
  botId: number;
  searchText: string;
};
const Link = ({ botId, searchText }: LinkProps) => {
  const { history } = useRouter();
  const dispatch: Dispatch<StoreState> = useDispatch();
  const onMainMenuLinkClick = () => dispatch(selectMainMenu());
  const linkUrl = `${routes.CHATBOT_EDITOR}/${botId}/build/main-menu`;
  return (
    <a
      href="#"
      title={searchText}
      onClick={e => {
        e.preventDefault();
        onMainMenuLinkClick();
        history.push(linkUrl);
      }}
    >
      {searchText}
    </a>
  );
};

const maybeWrapWithLink = (note: string | undefined, botId: number) => {
  const searchText = 'Main Menu';
  if (note && note.indexOf(searchText) >= 0) {
    const newNote = note.replace(searchText, '');
    return {
      note: newNote,
      link: <Link searchText={searchText} botId={botId} />,
    };
  }
  return { note };
};

type Props = { inSection?: boolean; className?: string };

export const ResponseSettings = ({
  inSection = true,
  className = '',
}: Props) => {
  const activeChatbotEntity = useSelector(activeChatbotSelector);
  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const freeOrSpecialPricePlan = useSelector(activeFacebookPagePlanInfoSelector)
    .info.isFreeOrSpecialPlan;
  const hasMainMenuItems = useSelector(
    (state: StoreState) => state.mainMenu.entities.length > 0,
  );
  const enablePreventInput = freeOrSpecialPricePlan || hasMainMenuItems;

  const { update: updateChatbot } = useChatbotCRUDActions();

  const [selectedOption, setSelectedOption] = useState(
    activeChatbotEntity.noMatchResponse,
  );

  useEffect(() => {
    if (activeChatbotEntity?.noMatchResponse === 'ai') {
      updateChatbot({
        ...activeChatbotEntity,
        noMatchResponse: DefaultResponseType.DefaultPage,
      });
    }
  });

  const mapSettings = () => {
    const handleChange = (type: DefaultResponseType) => () => {
      setSelectedOption(type);
      const updatedChatbot = {
        ...activeChatbotEntity,
        noMatchResponse: type as DefaultResponseType,
      };
      updateChatbot(updatedChatbot);
    };
    const result: JSX.Element[] = [];
    const isDefault = type => {
      return (
        type === DefaultResponseType.DefaultPage ||
        type === DefaultResponseType.DefaultPageOncePerSession
      );
    };
    for (const { id, type, text, notes } of noMatchResponsesMap.values()) {
      if (id < 4) {
        const optionProps = {
          handleChange: handleChange(type),
          // TODO What Kind of Sorcery Is This? Refactor all the component and
          // use appropriate design for data
          checked: isDefault(type)
            ? isDefault(selectedOption)
            : selectedOption === type,
          text,
          note: <NoteWithLink {...maybeWrapWithLink(notes, activeChatbotId)} />,
          disabled: !!(
            notes &&
            notes.indexOf('Main Menu') > -1 &&
            !enablePreventInput
          ),
          isDefaultResponse: isDefault(type),
          value: selectedOption,
          onSelectChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            handleChange(e.target.value as DefaultResponseType)();
          },
        };
        result.push(<ResponseOption {...optionProps} />);
      }
    }
    return result;
  };

  const mainContent = () => [
    <FormGroup className="response-settings__input-container" key="form-group">
      {mapSettings()}
    </FormGroup>,
    <ChatBotQandAOptions
      key="qna-options"
      chatbot={activeChatbotEntity}
      updateChatbot={updateChatbot}
    />,
  ];
  const TITLE = 'Q&A Settings';
  return inSection ? (
    <SettingSection title={TITLE}>{mainContent()}</SettingSection>
  ) : (
    <div className={className}>
      <div className={classnames(className, 'response-settings-title')}>
        {TITLE}
      </div>
      {mainContent()}
    </div>
  );
};
